/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { FittingTreeView } from '../../../models/fitting-tree-view-vm';
import {
  FITTING_TREE_VIEW_FACADE_TOKEN,
  IFittingTreeViewFacade,
} from '../../../../../../../toolbelt/data-access/src/lib/domain/facades-models/fitting-tree-view-facade-interface';
import { DxTreeViewComponent } from 'devextreme-angular';
import { GlobalConstants } from '../../../constants/global-constants';
import dxTreeView from 'devextreme/ui/tree_view';

@Component({
  selector: 'sysque-fitting-tree-view',
  templateUrl: './fitting-tree-view.component.html',
  styleUrls: ['./fitting-tree-view.component.scss'],
  standalone: false
})
export class FittingTreeViewComponent implements OnInit {
  @ViewChild(DxTreeViewComponent) treeView: DxTreeViewComponent;
  @ViewChild('treeContainer', { static: false }) treeContainer: ElementRef;
  public dataSource: FittingTreeView[] = [];
  public previousScrollPosition: number = 0;
  private previousSelectedChildId = [];
  private treeViewInstance: dxTreeView<any>;

  constructor(@Inject(FITTING_TREE_VIEW_FACADE_TOKEN) public fittingTreeViewFacade: IFittingTreeViewFacade) {}

  public ngOnInit(): void {
    this.fittingTreeViewFacade.getTreeViewItems().subscribe((items) => {
      this.dataSource = items;
    });

    this.fittingTreeViewFacade.getLocalStorageUpdate().subscribe((key) => {
      if (key === GlobalConstants.SELECTED_TAB_TREE_VIEW) {
        this.treeView.instance.collapseAll();
        this.treeViewInstance = this.treeView.instance;
      }
      this.fittingTreeViewFacade.setFittingTreeViewInstance(this);
    });
    this.registerEvents();
  }

  public ngAfterViewInit(): void {
    this.treeViewInstance = this.treeView.instance;
    this.registerEvents();
  }

  private registerEvents(): void {
    if (this.treeViewInstance) {
      this.treeViewInstance.on('itemExpanded', (e) => this.handleItemAction(e, 'expand'));
      this.treeViewInstance.on('itemCollapsed', (e) => this.handleItemAction(e, 'collapse'));
    }
  }

  public handleItemAction(e: any, action: 'expand' | 'collapse'): void {
    this.previousScrollPosition = this.treeContainer.nativeElement.scrollTop;
    this.previousSelectedChildId = e.node.items;

    if (action === 'expand') {
      this.onItemExpanded(e.node);
    } else if (action === 'collapse') {
      this.onItemCollapsed(e.node);
    }

    setTimeout(() => {
      this.treeContainer.nativeElement.scrollTop = this.previousScrollPosition;
    }, 0);
  }

  public async onItemExpanded(node: any): Promise<void> {
    this.fittingTreeViewFacade.updateSelectorForResult();
    let item = node.itemData;
    item = await this.expandItem(item);
    this.dataSource = [...this.dataSource];

    const validItems = (item as FittingTreeView).items.filter((x) => x.image !== null && !x.isFolder);
    if (item.scheduleID != null || item.id.includes('\\Custom\\') || item.id.includes('\\Assemblies\\')) {
      this.fittingTreeViewFacade.getResultInformationFromTreeViewFolder(validItems);
    }
    localStorage.setItem('selectorForResult', 'treeView');
    if (this.treeView && this.treeView.instance) {
      setTimeout(() => {
        this.treeView.instance.scrollToItem(item);
      }, 0);
    }
  }

  public async expandItem(item: FittingTreeView): Promise<FittingTreeView> {
    // Only expand items that have a scheduleID
    if (item.scheduleID !== null) {
      // Fetch the updated node data
      const result = await this.fittingTreeViewFacade.updateTreeViewNodeOnExpansionChanged(item);
      const currentItem = item;

      // Ensure we are updating the correct node
      if (currentItem.id === result.id) {
        const uniqueItems: FittingTreeView[] = [];
        const seenIds = new Set<string>();

        // Iterate through the returned child items
        for (const child of result.items) {
          // Only add children with unique IDs to avoid duplicates in the tree
          if (!seenIds.has(child.id)) {
            child.expanded = true;
            uniqueItems.push(child);
            seenIds.add(child.id);
          }
        }

        currentItem.items = uniqueItems;
      }
    }

    // Assign the filtered, unique children to the current item's items property
    return item;
  }

  public onItemClick(e: any): void {
    //show each fitting information file
    this.treeView.instance.unselectAll();

    const item = e.itemData === undefined ? e : e.itemData;

    if (!item.isFolder) {
      this.fittingTreeViewFacade.getResultInformationFromTreeViewFolder([item]);
    } else {
      const validItems = (item as FittingTreeView).items.filter((x) => x.image !== null && !x.isFolder);
      this.fittingTreeViewFacade.getResultInformationFromTreeViewFolder(validItems);
    }
  }

  public onItemCollapsed(node: any): void {
    const selectedNode = node.itemData.items;

    this.fittingTreeViewFacade.getResultInformationFromTreeViewFolder([]);
    if (selectedNode) {
      selectedNode.forEach((child) => {
        child.expanded = false;
        this.treeView.instance.collapseItem(child.id);
        this.treeView.instance.unselectItem(child.id);
      });
    }
  }

  public showInEnclosingFolder(item: FittingTreeView): void {
    this.treeView.instance.scrollToItem(item);
  }
}
